import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import {
  InvoiceStatusReportFinalizeValidation,
  InvoiceStatusReportStageValidation,
  useFinalizeInvoiceStatusReport,
  useStageInvoiceStatusReport,
} from "@/lib/api/otc";
import { MimeType } from "@/lib/api/utils";
import { UploadPresignedFile } from "@/lib/file";
import { ReportCard, ReportDownloadLink } from "./ReportCard";

/**
 * Invoice Status Report — the user uploads the invoice export (.xlsx) and may
 * include a previous finalized output containing notes.
 *
 * Flow: stage (presigned PUT URLs) -> upload source files to S3 -> finalize.
 */
export function InvoiceStatusReportCard() {
  const { toast } = useToast();
  const stageReport = useStageInvoiceStatusReport();
  const finalizeReport = useFinalizeInvoiceStatusReport();
  const [file, setFile] = useState<File | null>(null);
  const [previousFinalOutputRun, setPreviousFinalOutputRun] = useState<File | null>(null);
  const [link, setLink] = useState<string | undefined>(undefined);
  const [isDownloading, setIsDownloading] = useState(false);

  const isDisabled = !file;

  const onGenerate = async () => {
    if (isDownloading || !file) return;
    setLink(undefined);
    setIsDownloading(true);

    try {
      const sourceFiles = [file, ...(previousFinalOutputRun ? [previousFinalOutputRun] : [])];
      const stageBody = InvoiceStatusReportStageValidation.safeParse({
        source: sourceFiles.map((sourceFile: File) => ({
          filename: sourceFile.name,
          content_type: MimeType.Xlsx,
        })),
      });
      if (!stageBody.success) {
        toast({ title: "Invalid file", description: "" });
        return;
      }

      const stageResult = await stageReport(stageBody.data);
      if (!stageResult.success) {
        throw stageResult;
      }

      if (stageResult.data.source.length !== sourceFiles.length) {
        throw new Error("Invoice Status stage response returned an unexpected number of sources");
      }
      for (const [index, sourceFile] of sourceFiles.entries()) {
        const stagedSource = stageResult.data.source[index];
        if (!stagedSource) {
          throw new Error(`Invoice Status stage response is missing source ${index}`);
        }
        const upload = await UploadPresignedFile(
          stagedSource.put_url,
          sourceFile,
          MimeType.Xlsx,
        );
        if (!upload.success) {
          throw upload;
        }
      }

      const finalizeBody = InvoiceStatusReportFinalizeValidation.safeParse({
        report_id: stageResult.data.report_id,
      });
      if (!finalizeBody.success) {
        throw finalizeBody.error;
      }

      const finalizeResult = await finalizeReport(finalizeBody.data);
      if (!finalizeResult.success) {
        throw finalizeResult;
      }
      setLink(finalizeResult.data.link);
    } catch (error: unknown) {
      console.error("Failed to generate otc invoice status report", error);
      toast({ title: "Failed to generate", description: "" });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ReportCard
      title="Invoice Status Report"
      description=""
      isDownloading={isDownloading}
      isGenerateDisabled={isDisabled}
      onGenerate={onGenerate}
      hasResults={!!link}
      linkContent={link ? <ReportDownloadLink href={link} label="Download Report" /> : null}
    >
      <div className="grid grid-cols-1 gap-3">
        <Label htmlFor="otc-invoice-status-report-file">Invoice Export (.xlsx)</Label>
        <Input
          id="otc-invoice-status-report-file"
          type="file"
          accept=".xlsx"
          disabled={isDownloading}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setLink(undefined);
            setFile(e.target.files?.[0] ?? null);
          }}
        />

        <Label htmlFor="otc-invoice-status-previous-output-file">
          Previous Final Output Run (.xlsx, optional)
        </Label>
        <Input
          id="otc-invoice-status-previous-output-file"
          type="file"
          accept=".xlsx"
          disabled={isDownloading}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setLink(undefined);
            setPreviousFinalOutputRun(e.target.files?.[0] ?? null);
          }}
        />
      </div>
    </ReportCard>
  );
}
